import React, { useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import { PostStatus } from '../../data-layer/posts/constants';
import * as postsService from '../../data-layer/posts/service';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconAlarm } from '../../ui/icon/IconAlarm';
import { IconClock } from '../../ui/icon/IconClock';
import { IconDeclinedFilled } from '../../ui/icon/IconDeclinedFilled';
import { Button } from '../../ui/input/Button';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { PostCard, PostCardSkeleton } from '../../ui/posts/PostCard/PostCard';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { PostStatistic, PostStatisticSkeleton } from '../../ui/posts/PostStatistic';
import { Typography } from '../../ui/typography/Typography';
import { hasAccessToPostStat } from '../../utils/accessService';
import { EventSource } from '../../utils/analyticsService';
import { useMeta } from '../../utils/useMeta';
import { useCurrentUser } from '../users/useCurrentUser';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { usePostModel } from './usePostModel';
import { withPostCallbacks } from './withPostCallbacks';
import type { PostModel, PostStatModel } from '../../data-layer/posts/types';

const MODERATION_RULES_LINK = 'https://yandex.ru/support2/rythm/ru/buisness/moderation';

const EnhancedPostCard = withPostCallbacks(PostCard, EventSource.POST);

const EnhancedPostMenu = withPostCallbacks(PostContextMenu, EventSource.POST);

const buildMeta = (model: PostModel | undefined, canonicalUrl: string) => {
    if (!model) {
        return {};
    }

    const {
        content: [
            {
                type: contentType,
                media: [firstMedia],
            },
        ],
    } = model;

    return {
        title: `${model.text.split(/\s+/).slice(0, 5).join(' ')}... - ${
            model.business.displayName
        } - Яндекс Ритм`,
        description: model.text,
        links: [{ rel: 'canonical', href: canonicalUrl }],
        openGraph: {
            title: `Яндекс Ритм | ${model.business.publicId}`,
            description: model.text,
            // @ts-ignore не прорастают type-guard-ы в contentType
            image: contentType === 'video' ? firstMedia.firstFrameUrl : firstMedia.url,
        },
    };
};

const renderAlertElement = (post: PostModel) => {
    if (post.status === PostStatus.DECLINED) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box p={1} width="100%" background="var(--background-secondary)" borderRadius={2}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1} mb={1}>
                        <IconDeclinedFilled size={24} color="var(--color-danger)" />
                        <Typography variant="caption" p={1}>
                            Пост отклонен{' '}
                            <Typography
                                as="a"
                                target="_blank"
                                display="inline"
                                href={MODERATION_RULES_LINK}
                                rel="nofollow noreferrer"
                                variant="caption"
                                color="link"
                            >
                                по правилам сервиса
                            </Typography>
                            . Отредактируйте и повторно отправьте на публикацию.
                        </Typography>
                    </Flex>
                    <Button
                        as={Link}
                        to={`/posts/${post.id}/edit`}
                        variant="filled"
                        width="100%"
                        color="secondary"
                    >
                        Отредактировать пост
                    </Button>
                </Box>
            </Box>
        );
    }

    if (post.status === PostStatus.MODERATION) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box width="100%" background="var(--background-secondary)" borderRadius={2} p={1}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1}>
                        <IconClock size={24} color="var(--color-primary)" />
                        <Typography variant="caption">
                            Пост находится на модерации. После одобрения, он появится
                            у пользователей
                        </Typography>
                    </Flex>
                </Box>
            </Box>
        );
    }

    if (post.status === PostStatus.LIMITED) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box width="100%" background="var(--background-secondary)" borderRadius={2} p={1}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1} mb={1}>
                        <IconAlarm size={24} color="var(--color-danger)" />
                        <Typography variant="caption" p={1}>
                            Данный пост доступен пользователям только в вашем аккаунте и не
                            участвует в рекомендательных процессах. Это может быть связано с
                            содержанием в посте возрастных тематик. Подробнее про правила сервиса
                            можно прочитать{' '}
                            <Typography
                                as="a"
                                target="_blank"
                                display="inline"
                                href={MODERATION_RULES_LINK}
                                rel="nofollow noreferrer"
                                variant="caption"
                                color="link"
                            >
                                по правилам сервиса
                            </Typography>
                            .
                        </Typography>
                    </Flex>
                    <Button
                        as={Link}
                        to={`/posts/${post.id}/edit`}
                        variant="filled"
                        width="100%"
                        color="secondary"
                    >
                        Отредактировать пост
                    </Button>
                </Box>
            </Box>
        );
    }

    return null;
};

interface PostStatisticProps {
    stats: PostStatModel;
}

interface PostDetailsLocationState {
    fromCollectionId?: string;
    fromBusinessId?: string;
}

export function PostDetails() {
    const params = useParams();
    const locationState = useLocation().state as PostDetailsLocationState | null;
    const postId = params.postId!;
    const post = usePostModel({ postId });
    const currentUser = useCurrentUser();
    const canonicalUrl = useCanonicalUrl();
    const [postStatistic, setPostStatistic] = useState<PostStatisticProps | undefined>(undefined);

    useMeta(buildMeta(post, canonicalUrl));

    if (post && post.status === PostStatus.DELETED) {
        return <Navigate to="/" replace />;
    }

    const { loading } = useAsync(async () => {
        setPostStatistic(undefined);
        if (hasAccessToPostStat(post?.business.id, currentUser)) {
            setPostStatistic(await postsService.loadPostStatistic(postId));
        }
    }, [postId, currentUser]);

    return (
        <DashboardPage>
            <Flex
                direction="column"
                width="100%"
                background="var(--background-primary)"
                borderRadius={{ sm: 2 }}
                overflow="hidden"
            >
                {post ? (
                    <Box width="100%">
                        {renderAlertElement(post)}
                        <EnhancedPostCard
                            index={0}
                            post={post}
                            borderRadius={0}
                            fromCollectionId={locationState?.fromCollectionId}
                            fromBusinessId={locationState?.fromBusinessId}
                            renderMenu={(trigger) => (
                                <EnhancedPostMenu
                                    fromCollectionId={locationState?.fromCollectionId}
                                    fromBusinessId={locationState?.fromBusinessId}
                                    index={0}
                                    trigger={trigger}
                                    post={post}
                                    user={currentUser}
                                />
                            )}
                        />
                    </Box>
                ) : (
                    <PostCardSkeleton />
                )}
            </Flex>
            <Box width="100%">
                {hasAccessToPostStat(post?.business.id, currentUser) &&
                    (loading ? (
                        <PostStatisticSkeleton />
                    ) : (
                        postStatistic?.stats && <PostStatistic statistic={postStatistic.stats} />
                    ))}
            </Box>
        </DashboardPage>
    );
}
